import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an unhandled error:", error, errorInfo);
  }

  public reset = () => {
    this.setState({ hasError: false, error: null });
  };

  public override render() {
    if (this.state.hasError) {
      return (
        <div
          className="error-boundary-fallback"
          style={{
            padding: "32px",
            textAlign: "center",
            margin: "24px auto",
            maxWidth: "600px",
            background: "var(--card-bg, #ffffff)",
            borderRadius: "8px",
            border: "1px solid var(--border-color, #e2e8f0)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              padding: "12px",
              borderRadius: "50%",
              background: "var(--danger-bg, #fee2e2)",
              color: "var(--danger-text, #dc2626)",
              marginBottom: "16px",
            }}
          >
            <AlertTriangle size={32} />
          </div>
          <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", color: "var(--text-primary, #0f172a)" }}>
            {this.props.fallbackTitle || "页面组件渲染遇到异常"}
          </h3>
          <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "var(--text-secondary, #64748b)" }}>
            {this.state.error?.message || "发生了未预期的前端错误。"}
          </p>
          <button
            type="button"
            className="secondary-button"
            onClick={this.reset}
            style={{ display: "inline-flex", alignItems: "center", gap: "8px", margin: "0 auto", cursor: "pointer" }}
          >
            <RefreshCw size={16} /> 重试恢复
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
